import { Link } from "react-router-dom"
import styled from "styled-components"

const Proof = styled.section`
  width: 100%;
  background: #ffffff;
  border-bottom: 1px solid #ccd8e2;
`

const ProofInner = styled.div`
  width: min(1280px, 92vw);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));

  div {
    padding: 1.3rem clamp(0.7rem, 2vw, 1.5rem);
  }

  div + div {
    border-left: 1px solid #d5dfe7;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #123858;
    font-size: 0.86rem;
  }

  span {
    margin-top: 0.3rem;
    color: #708292;
    font-size: 0.72rem;
    line-height: 1.45;
  }

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    div:nth-child(3) {
      border-left: 0;
    }

    div:nth-child(n + 3) {
      border-top: 1px solid #d5dfe7;
    }
  }
`

const Section = styled.section`
  width: 100%;
  padding: clamp(5rem, 10vw, 9rem) 0;
  background: #f0f5f9;
`

const Inner = styled.div`
  width: min(1280px, 90vw);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(420px, 1.08fr);
  gap: clamp(3rem, 8vw, 8rem);
  align-items: center;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`

const Eyebrow = styled.p`
  margin: 0 0 0.8rem;
  color: #337abd;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`

const Title = styled.h2`
  max-width: 690px;
  color: #102f4f;
  font-size: clamp(2.5rem, 5vw, 4.8rem);
  line-height: 0.98;
  letter-spacing: -0.055em;
`

const Intro = styled.p`
  max-width: 650px;
  margin: 1.4rem 0 0;
  color: #5b7184;
  line-height: 1.7;
`

const Steps = styled.ol`
  margin: 2rem 0 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid #c5d3de;

  li {
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr);
    gap: 1rem;
    padding: 1rem 0;
    border-bottom: 1px solid #c5d3de;
  }

  b {
    color: #337abd;
    font-size: 0.72rem;
    letter-spacing: 0.08em;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #193a55;
  }

  span {
    margin-top: 0.3rem;
    color: #687d8e;
    font-size: 0.83rem;
    line-height: 1.5;
  }
`

const Portal = styled.div`
  position: relative;
  padding: clamp(1.4rem, 4vw, 2.4rem);
  border: 1px solid #30536e;
  border-top: 4px solid #63b5e4;
  background: #071a2e;
  color: #fff;
  box-shadow: 26px 32px 0 #d8e4ec;

  @media (max-width: 600px) {
    box-shadow: 12px 14px 0 #d8e4ec;
  }
`

const PortalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);

  strong {
    font-size: 1.05rem;
  }

  span {
    color: #83c3ea;
    font-size: 0.72rem;
    font-weight: 750;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }
`

const PortalTitle = styled.h3`
  margin: 2rem 0 0;
  color: #fff;
  font-size: clamp(1.8rem, 4vw, 3rem);
  line-height: 1.05;
  letter-spacing: -0.04em;
`

const PortalText = styled.p`
  margin: 0.8rem 0 1.5rem;
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.6;
`

const RequestRows = styled.div`
  display: grid;
  border-top: 1px solid rgba(255, 255, 255, 0.16);

  div {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 1rem;
    padding: 0.9rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.16);
  }

  strong {
    font-size: 0.84rem;
  }

  span {
    color: #8fc8ea;
    font-size: 0.68rem;
    font-weight: 750;
    text-transform: uppercase;
  }
`

const PortalLink = styled(Link)`
  display: inline-flex;
  min-height: 48px;
  align-items: center;
  margin-top: 1.5rem;
  padding: 0 1.1rem;
  border: 1px solid #63b5e4;
  color: #fff;
  font-size: 0.84rem;
  font-weight: 750;
  text-decoration: none;

  &:hover {
    background: #1d6da6;
  }
`

export const ProofStrip = () => (
  <Proof aria-label="Repères sur le service">
    <ProofInner>
      <div>
        <strong>Équipe à Neuville</strong>
        <span>Portneuf et couronne ouest de Québec</span>
      </div>
      <div>
        <strong>Mandat sur mesure</strong>
        <span>Responsabilités et autorisations définies</span>
      </div>
      <div>
        <strong>Urgences 7 j/7</strong>
        <span>Protocole d’appel pour les locataires</span>
      </div>
      <div>
        <strong>Espace TB</strong>
        <span>Demandes, photos et suivis au même endroit</span>
      </div>
    </ProofInner>
  </Proof>
)

const ProcessSection = () => (
  <Section id="processus">
    <Inner>
      <div>
        <Eyebrow>Une transition qui reste maîtrisée</Eyebrow>
        <Title>Une méthode claire avant même la première demande.</Title>
        <Intro>
          La qualité d’une gestion commence par des responsabilités bien définies. Nous établissons le cadre avec vous, puis nous rendons les suivis
          faciles à retrouver.
        </Intro>
        <Steps>
          <li>
            <b>01</b>
            <div>
              <strong>Comprendre l’immeuble</strong>
              <span>Portes, occupants, fournisseurs, échéances et priorités.</span>
            </div>
          </li>
          <li>
            <b>02</b>
            <div>
              <strong>Définir le mandat</strong>
              <span>Tâches déléguées, seuils d’autorisation et fréquence des suivis.</span>
            </div>
          </li>
          <li>
            <b>03</b>
            <div>
              <strong>Préparer la transition</strong>
              <span>Coordonnées, documents utiles et consignes communiquées aux bonnes personnes.</span>
            </div>
          </li>
          <li>
            <b>04</b>
            <div>
              <strong>Suivre dans Espace TB</strong>
              <span>Demandes, photos, messages, statuts et rapports selon votre accès.</span>
            </div>
          </li>
        </Steps>
      </div>
      <Portal aria-label="Aperçu de l’Espace TB">
        <PortalHeader>
          <strong>Espace TB</strong>
          <span>Web · mobile · installable</span>
        </PortalHeader>
        <PortalTitle>Chaque demande garde son contexte.</PortalTitle>
        <PortalText>Un espace simple pour les locataires, les propriétaires et l’équipe, avec les fonctions adaptées à chaque rôle.</PortalText>
        <RequestRows>
          <div>
            <strong>Réparation · unité 4</strong>
            <span>En analyse</span>
          </div>
          <div>
            <strong>Entretien saisonnier</strong>
            <span>Planifiée</span>
          </div>
          <div>
            <strong>Accès et clés</strong>
            <span>Terminée</span>
          </div>
        </RequestRows>
        <PortalLink to="/se-connecter">Accéder à Espace TB →</PortalLink>
      </Portal>
    </Inner>
  </Section>
)

export default ProcessSection
